export enum ErrorType {
    API_FAILURE = 'API_FAILURE',
    REQUEST_TIMEOUT = 'REQUEST_TIMEOUT',
    RESPONSE_DECODING_FAILURE = 'RESPONSE_DECODING_FAILURE',
    DNS_LOOKUP_FAILURE = 'DNS_LOOKUP_FAILURE',
    SSL_TLS_HANDSHAKE_FAILURE = 'SSL_TLS_HANDSHAKE_FAILURE',
    OTHER_NETWORK_FAILURE = 'OTHER_NETWORK_FAILURE',
}
export interface ResponseErrorSLP {
    url: string;
    code: string;
    subCode: number;
    description: string;
    trackingEvent: string;
    errorDomain: string;
    severity: string;
    apiIdentifier: string;
    message: string;
    status: number;
    statusText: string;
    errorFields: Array<any>;
}

type ErrorData = {
    statusCode?: number;
    code: string;
    subCode: number;
    msg: string;
    description: string;
    trackingEvent: string;
    errorDomain: string;
    severity: string;
    apiIdentifier: string;
};

export type NetworkErrorInfo = {
    errorType: ErrorType;
    url?: string;
    statusCode?: number;
    errorData?: Partial<ErrorData>;
    networkErrorMessage?: string;
};

/**
 * Strips query parameters from a URL to anonymize potential PII data.
 */
export const anonymizeUrl = (url: string): string => {
    try {
        const parsed = new URL(url);
        parsed.search = '';
        return parsed.toString();
    } catch {
        return url.split('?')[0];
    }
};

/**
 * Builds a Mixpanel event payload for the given network error, including only
 * the attributes relevant to the specific error type as per the analytics spec.
 */
export const buildErrorTrackingPayload = (info: NetworkErrorInfo): Record<string, unknown> => {
    const base: Record<string, unknown> = {
        'Error Type': info.errorType,
    };

    switch (info.errorType) {
        case ErrorType.API_FAILURE:
            return {
                ...base,
                'Error Message': info.errorData?.msg,
                'Error Domain': info.errorData?.errorDomain,
                'Error Code': info.errorData?.code,
                'Error SubCode': info.errorData?.subCode,
                'Error Response Status': info.statusCode,
                'Error Severity': info.errorData?.severity,
                'Error API Identifier': info.errorData?.apiIdentifier,
                'Error API URL': info.url ? anonymizeUrl(info.url) : undefined,
                // Retained for cross-platform dashboard compatibility
                'HTTP Error Code': info.statusCode,
                'Response Error Code': info.errorData?.code,
                'Response Error Message': info.errorData?.msg,
            };
        case ErrorType.RESPONSE_DECODING_FAILURE:
            return {
                ...base,
                'Error Response Status': info.statusCode,
                'Error API URL': info.url ? anonymizeUrl(info.url) : undefined,
                'HTTP Error Code': info.statusCode,
            };
        case ErrorType.OTHER_NETWORK_FAILURE:
            return {
                ...base,
                'Network Failure Error Message': info.networkErrorMessage,
            };
        default:
            return base;
    }
};

export const apiErrorMapper = (error: ErrorData) => {
    return {
        'Error Code': error.code,
        'Error Sub Code': error.subCode ?? '',
        // 'Error Status Code': error.statusCode ?? '',
        'Error Message': error.msg,
        'Error Description': error.description,
        'Error Domain': error.errorDomain,
        'Tracking Event': error.trackingEvent,
        Severity: error.severity,
        'API Identifier': error.apiIdentifier,
    };
};

export const trackSLPError = (err: any): ResponseErrorSLP | null => {
    if (!err || !err.data) return null;
    const errorData = { ...err.data } as ResponseErrorSLP;
    if (typeof errorData?.trackingEvent === 'string') {
        const mixpanelPayload = errorData;
        if (typeof mixpanelPayload?.url === 'string') {
            mixpanelPayload.url = anonymizeUrl(mixpanelPayload.url);
        }
        return mixpanelPayload;
    }
    return null;
};
